import { Link, createFileRoute, useLocation } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import {
  LabPageHeader,
  LabSection,
  StatusLabel,
  TaggedPosts,
} from '@/components/lab/LabBlocks'
import { LedgerSpecRow } from '@/components/ledger/LedgerBlocks'
import { localizedPageHead } from '@/lib/seo-head'
import { LINKS } from '@/lib/company'
import { getLocaleFromPath, withLocalePath } from '@/lib/i18n-utils'

export const Route = createFileRoute('/{-$locale}/research')({
  head: ({ params }) => localizedPageHead('research', params.locale),
  component: ResearchPage,
})

export function ResearchPage() {
  const { t } = useTranslation('lab')
  const locale = getLocaleFromPath(useLocation().pathname)
  const bosnian = locale === 'bs-BA'
  return (
    <div className="min-h-screen">
      <LabPageHeader
        eyebrow={t('research.eyebrow')}
        title={t('research.title')}
        intro={t('research.intro')}
      />
      <LabSection title={t('research.upstream.title')}>
        <p>{t('research.upstream.body')}</p>
        <dl className="ledger-spec">
          <LedgerSpecRow term={t('research.evidence.source')}>
            {t('research.evidence.patchSource')}
          </LedgerSpecRow>
          <LedgerSpecRow term={t('research.evidence.validation')}>
            {t('research.evidence.patchValidation')}
          </LedgerSpecRow>
        </dl>
        <ul className="space-y-2">
          <li>
            <a
              className="inline-flex min-h-11 items-center underline underline-offset-4"
              href={LINKS.tilelangIssue}
              rel="noopener"
              target="_blank"
            >
              {t('research.upstream.issue')}
            </a>
          </li>
          <li className="flex flex-wrap items-center gap-3">
            <a
              className="inline-flex min-h-11 items-center underline underline-offset-4"
              href={LINKS.tilelangPr}
              rel="noopener"
              target="_blank"
            >
              {t('research.upstream.pr')}
            </a>
          </li>
        </ul>
        <p className="text-sm">{t('research.evidence.patchStatus')}</p>
      </LabSection>
      <LabSection title={t('research.kernels.title')}>
        <p>{t('research.kernels.body')}</p>
        <dl className="ledger-spec">
          <LedgerSpecRow term={t('research.evidence.measurement')}>
            <span className="font-mono">{t('research.kernels.value')}</span>
          </LedgerSpecRow>
          <LedgerSpecRow term={t('research.evidence.validation')}>
            {t('research.kernels.caption')}
          </LedgerSpecRow>
        </dl>
      </LabSection>
      <LabSection title={t('research.speculative.title')}>
        <p>{t('research.speculative.body')}</p>
        <StatusLabel status="research" />
      </LabSection>
      <LabSection title={t('research.training.title')}>
        <p>{t('research.training.body')}</p>
        <StatusLabel status="research" />
      </LabSection>
      <LabSection
        title={bosnian ? 'Razvijajte ideje s nama' : 'Develop ideas with us'}
      >
        <p>
          {bosnian
            ? 'Podijelite pitanje, kritiku metode, pokušaj reprodukcije ili poboljšanje otvorenog koda. Doprinosi zadržavaju autorstvo; objava nije naučna potvrda rezultata.'
            : 'Share a question, method critique, reproduction attempt or open-source improvement. Contributions retain authorship; publication does not validate research findings.'}
        </p>
        <ul className="space-y-3">
          <li>
            <Link
              className="inline-flex min-h-11 items-center underline underline-offset-4"
              to={withLocalePath('/community', locale) as any}
              search={{ view: 'discussions' } as any}
              hash="community-browser"
            >
              {bosnian
                ? 'Istražite diskusije zajednice'
                : 'Explore community discussions'}
            </Link>
          </li>
          <li>
            <Link
              className="inline-flex min-h-11 items-center underline underline-offset-4"
              to={
                withLocalePath(
                  '/community/598c91f2-fe29-4cba-95ea-6649e04ae3ad',
                  locale,
                ) as any
              }
            >
              {bosnian
                ? 'Doprinesite prijedlogu provjere kvaliteta skupova podataka'
                : 'Contribute to the dataset-quality method proposal'}
            </Link>
          </li>
          <li>
            <a
              className="inline-flex min-h-11 items-center underline underline-offset-4"
              href="/agents/research/SKILL.md"
            >
              {bosnian
                ? 'Vodič za doprinos AI agenata'
                : 'AI agent participation guide'}
            </a>
          </li>
        </ul>
        <p className="text-sm">
          {bosnian
            ? 'Prijedlog kvaliteta podataka je metod u razvoju. Još nije pokazano poboljšanje stvarnog skupa podataka ili performansi modela.'
            : 'The dataset-quality proposal is a method in development. No improvement to a real dataset or model performance has been demonstrated.'}
        </p>
      </LabSection>
      <LabSection title={t('research.evidence.standardTitle')}>
        <p>{t('research.evidence.standardBody')}</p>
        <Link
          className="inline-flex min-h-11 items-center underline underline-offset-4"
          to={withLocalePath('/trust', locale) as any}
        >
          {t('research.evidence.standardLink')}
        </Link>
      </LabSection>
      <LabSection title={t('research.cta.title')}>
        <Button asChild size="lg">
          <Link
            to={withLocalePath('/contact', locale) as any}
            search={{ topic: 'research' } as any}
          >
            {t('research.cta.button')}
          </Link>
        </Button>
      </LabSection>
      <TaggedPosts tag="research" title={t('research.postsTitle')} />
    </div>
  )
}
